// ============================================================================
// GsMobileBottomNav — barre d'onglets fixe mobile (UI_STANDARDS §17 mobile-first)
// ============================================================================
// Renaissance INV (BLITZ-INV-SCAFFOLD 2026-06-15, SPEC-INV D6) : premiere
// implementation reelle d'un composant mobile partage. Remplace l'ancien
// MobileBottomNav minimal (alias deprecated conserve 1 release). Les renaissances
// suivantes (SST...) sont mobiles aussi -> @gs/ui des la v1.
//
// Memes items que GsSidebar (icone Lucide + label + badge optionnel) : « le bord
// dit l'etat ». Actif = brand-* (wayfinding, GS_SEMANTIC_COLORS.navActive*) ;
// inactif = slate. Badge (item.badge) en pastille coin sup-droit (pattern
// GsNotificationBell). FAB central optionnel (CTA principal terrain).
//
// A11y : <nav aria-label> (prop navLabel, defaut FR — JAMAIS de t() dans @gs/ui),
// onglet actif aria-current="page", icones aria-hidden, badge avec libelle SR.
// Cible tactile 44px + touch-manipulation. AUCUNE couleur en dur (tokens brand/slate).

import type { LucideIcon } from 'lucide-react';
import type { SidebarItem } from './GsSidebar';

export interface GsMobileBottomNavFab {
  /** Icone Lucide du bouton d'action principal. */
  icon: LucideIcon;
  /** Libelle accessible (aria-label) du FAB. */
  label: string;
  onClick: () => void;
}

export interface GsMobileBottomNavProps {
  items: SidebarItem[];
  activePath: string;
  onNavigate: (path: string) => void;
  /** aria-label de la barre (i18n par prop — defaut FR). */
  navLabel?: string;
  /** Bouton d'action central optionnel (FAB) — CTA principal terrain. */
  fab?: GsMobileBottomNavFab;
}

function formatBadge(badge: string | number | undefined): string | null {
  if (badge === undefined || badge === null) return null;
  if (typeof badge === 'number') {
    if (badge <= 0) return null;
    return badge > 9 ? '9+' : String(badge);
  }
  const trimmed = badge.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Barre d'onglets fixe en bas d'ecran — mobile uniquement (md:hidden).
 * Affiche au plus ~4 onglets (UI_STANDARDS §17) + un FAB central optionnel.
 */
export function GsMobileBottomNav({
  items,
  activePath,
  onNavigate,
  navLabel = 'Navigation mobile',
  fab,
}: GsMobileBottomNavProps) {
  const visible = items.filter((item) => !item.adminOnly);

  return (
    <nav
      aria-label={navLabel}
      className="fixed bottom-0 left-0 right-0 z-40 flex min-h-15 items-center justify-around border-t border-slate-200 bg-white pb-safe shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] dark:border-slate-700 dark:bg-slate-900 dark:shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.25)] md:hidden"
    >
      {fab && (
        // FAB central, sureleve au-dessus de la barre (CTA principal terrain).
        <button
          type="button"
          aria-label={fab.label}
          onClick={fab.onClick}
          className="absolute left-1/2 top-0 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 touch-manipulation items-center justify-center rounded-full bg-brand-500 text-white shadow-md transition-colors hover:bg-brand-600 active:bg-brand-700"
        >
          <fab.icon size={26} strokeWidth={2.4} aria-hidden />
        </button>
      )}

      {visible.map((item) => {
        const Icon = item.icon;
        const isExternal = item.externalUrl === true;
        const isActive =
          !isExternal &&
          (activePath === item.path ||
            (item.path !== '/' && activePath.startsWith(item.path)));
        const badge = formatBadge(item.badge);
        const tone = isActive
          ? 'text-brand-500 dark:text-brand-400'
          : 'text-slate-400 dark:text-slate-500';

        return (
          <button
            key={item.key}
            type="button"
            aria-current={isActive ? 'page' : undefined}
            onClick={() => {
              if (isExternal) {
                window.open(item.path, '_blank', 'noopener,noreferrer');
                return;
              }
              onNavigate(item.path);
            }}
            className="relative flex min-h-11 min-w-11 touch-manipulation flex-col items-center justify-center gap-0.5 px-2 py-1"
          >
            <span className="relative inline-flex">
              <Icon size={22} strokeWidth={isActive ? 2.6 : 2} className={tone} aria-hidden />
              {badge && (
                <span className="absolute -right-2 -top-1.5 inline-flex min-w-4 items-center justify-center rounded-full bg-brand-500 px-1 text-chip font-bold leading-[15px] text-white">
                  <span className="sr-only">{`${item.label} : `}</span>
                  {badge}
                </span>
              )}
            </span>
            <span className={`max-w-20 truncate text-center text-chip font-medium leading-tight ${tone}`}>
              {item.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
